// === merge two profiling call trees =========================================

/**
 * Построение ключа для сопоставления узлов из разных замеров.
 * Можно заменить на свою логику (например, добавить/убрать поля).
 */
function defaultKeyFn(n) {
  const file = n.file ?? '';
  const name = n.name ?? '';
  const line = n.line ?? 0;
  const binding = n.binding ?? '';
  const widget_id = n.widget_id ?? '';
  return `${file}|${name}|${line}|${binding}|${widget_id}`;
}

/**
 * Глубокая копия узла (без ссылок на исходные объекты),
 * чтобы не мутировать исходные дерева.
 */
function cloneNode(n) {
  return {
    ...n,
    func_text: Array.isArray(n.func_text) ? n.func_text.map(x => ({ ...x })) : [],
    attrs: Array.isArray(n.attrs) ? n.attrs.map(cloneNode) : [],
  };
}

/**
 * Слияние массивов строк func_text с устранением дублей (по JSON-представлению).
 */
function mergeFuncText(a = [], b = []) {
  const seen = new Set();
  const out = [];
  for (const row of [...a, ...b]) {
    const key = JSON.stringify(row);
    if (!seen.has(key)) {
      seen.add(key);
      out.push(row);
    }
  }
  return out;
}

/**
 * Слияние двух узлов (dst мутируется), рекурсивно объединяя детей.
 * totalsMode: 'sum' (по умолчанию) или 'max' — как объединять total/self.
 */
function mergeNode(dst, src, { keyFn, totalsMode = 'sum' }) {
  // Численные поля
  const sumOrMax = (a, b) =>
    totalsMode === 'max' ? Math.max(a || 0, b || 0) : (a || 0) + (b || 0);

  dst.total_ms = sumOrMax(dst.total_ms, src.total_ms);
  dst.self_ms  = sumOrMax(dst.self_ms,  src.self_ms);
  dst.calls    = (dst.calls || 0) + (src.calls || 0);

  // Переносим/сохраняем метаданные (если их нет у dst)
  for (const k of ['file', 'name', 'line', 'binding', 'widget_id']) {
    if (dst[k] == null && src[k] != null) dst[k] = src[k];
  }

  // _fe: берём из dst, если уже есть; иначе — из src
  if (dst._fe == null && src._fe != null) dst._fe = { ...src._fe };

  // func_text — объединяем без дублей
  dst.func_text = mergeFuncText(dst.func_text, src.func_text);

  // Дети: строим индекс по ключу и мержим рекурсивно
  const key = keyFn || defaultKeyFn;

  const dstMap = new Map();
  dst.attrs = dst.attrs || [];
  for (const c of dst.attrs) dstMap.set(key(c), c);

  const srcAttrs = Array.isArray(src.attrs) ? src.attrs : [];
  for (const c of srcAttrs) {
    const cKey = key(c);
    const dstChild = dstMap.get(cKey);
    if (dstChild) {
      mergeNode(dstChild, c, { keyFn: key, totalsMode });
    } else {
      const copy = cloneNode(c);
      dst.attrs.push(copy);
      dstMap.set(cKey, copy);
    }
  }

  return dst;
}

/**
 * Пересчитать percent у детей узла: доля времени от total_ms родителя.
 * Сортировать детей по total_ms (desc). Рекурсивно.
 */
function recomputePercents(node) {
  const kids = Array.isArray(node.attrs) ? node.attrs : [];
  const parentTotal = node.total_ms || 0;

  for (const child of kids) {
    // percent ребёнка — доля total_ms от total_ms родителя (если родитель > 0)
    child.percent = parentTotal > 0 ? +(child.total_ms / parentTotal).toFixed(3) : 0;
    recomputePercents(child);
  }

  // сортировка детей
  kids.sort((a, b) => (b.total_ms || 0) - (a.total_ms || 0));
}

/**
 * Объединить два дерева (массивы корней).
 * @param {Array} treeA
 * @param {Array} treeB
 * @param {Object} options { keyFn, totalsMode }
 * @returns {Array} новое объединённое дерево
 */
export function mergeTrees(treeA, treeB, options = {}) {
  const key = options.keyFn || defaultKeyFn;
  const totalsMode = options.totalsMode || 'sum';

  const out = [];
  const index = new Map();

  // Сначала копируем A
  for (const n of (treeA || [])) {
    const copy = cloneNode(n);
    const k = key(copy);
    out.push(copy);
    index.set(k, copy);
  }

  // Мержим/добавляем B
  for (const n of (treeB || [])) {
    const k = key(n);
    const dst = index.get(k);
    if (dst) {
      mergeNode(dst, n, { keyFn: key, totalsMode });
    } else {
      const copy = cloneNode(n);
      out.push(copy);
      index.set(k, copy);
    }
  }

  // Пересчёт процентов и сортировка
  for (const root of out) {
    // процент у корня имеет мало смысла, но можно поставить 1.0
    root.percent = 1.0;
    recomputePercents(root);
  }
  // сортировка корней по total_ms
  out.sort((a, b) => (b.total_ms || 0) - (a.total_ms || 0));

  return out;
}

// === пример использования =====================================================
// const merged = mergeTrees(feTree, beTree, {
//   totalsMode: 'sum',                 // или 'max'
//   keyFn: (n) => `${n.file}|${n.name}|${n.line}|${n.widget_id||''}`,
// });